import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import NetworkError from 'shared/exceptions/network';
import {
  PollsSchema,
  TImagePlace,
  TAnswerMedia,
  ClickPoint,
  TSinglePollDetails,
} from '../types/pollsSchema';
import {
  fetchPollDetails,
  postPollBook,
  postPollStart,
  postPollResults,
  fetchPollsList,
  fetchPollRun,
  fetchTargetQuestion,
  fetchTargetAnswer,
  postPollRefuse,
} from '../services/pollDetailsService';
import { mappedSingleTestData } from '../HOC/mappedSingleTestData';
import { mappedCompositeTestData } from '../HOC/mappedCompositeTestData';
import {
  buildPollResultFromPart,
  enrichPollPart,
  getAnswerStage,
  partNeedsAnswer,
  pointsFromPart,
  withReworkBaseline,
} from '../helpers/pollRework';
import CONFIG from 'shared/constants/config';
import { safeJsonParse } from 'shared/utils/utils';
import { TEST_COUNT } from 'shared/constants/commmon';

const keepStoredAnswerMedia = (
  media: TAnswerMedia | undefined,
  serverMedia: TAnswerMedia | undefined,
): TAnswerMedia | undefined => {
  if (serverMedia) {
    return serverMedia;
  }

  if (!media?.link || media.link.startsWith('blob:')) {
    return undefined;
  }

  return media;
};

const initialState: PollsSchema = {
  pollDetails: {
    loading: false,
  },
  surveyResults: {
    stage: 0,
    loading: false,
    isSended: false,
  },
  points: {},
  pollBook: {
    loading: false,
  },
  pollRefuse: {
    loading: false,
  },
  pollsList: {
    loading: false,
    init: false,
  },
  pollRun: {
    data: null,
    loading: false,
  },
  target: {
    data: null,
    needAsk: false,
    loading: false,
    answer: false,
    answerLoading: false,
  },
};

export const pollsSlice = createSlice({
  name: 'polls',
  initialState,
  reducers: {
    clearSended(state) {
      state.surveyResults.isSended = false;
    },
    clearRefuseError(state) {
      state.pollRefuse.error = undefined;
    },
    clearPollBookError(state) {
      state.pollBook.error = undefined;
    },
    setStage(state, action: PayloadAction<number>) {
      if (!state.pollDetails.data || !('composite' in state.pollDetails.data)) {
        return;
      }

      const composite = state.pollDetails.data.composite;
      const nextStage = action.payload;
      if (
        !Number.isInteger(nextStage) ||
        nextStage < 0 ||
        nextStage >= composite.length ||
        nextStage === state.surveyResults.stage
      ) {
        return;
      }

      state.surveyResults.stage = nextStage;
      const result = {
        ...state.surveyResults,
        points: state.points,
        images: composite.map(item => item.images),
      };
      localStorage.setItem(CONFIG.POLL_PROCESS, JSON.stringify(result));
    },
    setSingleTestImageBlure(state, action: PayloadAction<string>) {
      if (state.pollDetails?.data && 'id' in state.pollDetails.data) {
        state.pollDetails.data.images = state.pollDetails.data.images.map(
          img => {
            if (String(img.id) === action.payload) {
              return { ...img, isBlurred: false };
            }
            return img;
          },
        );
      }
    },
    setSingleTestImageAdult(state, action: PayloadAction<string>) {
      if (state.pollDetails?.data && 'id' in state.pollDetails.data) {
        state.pollDetails.data.images = state.pollDetails.data.images.map(
          img => {
            if (String(img.id) === action.payload) {
              return { ...img, isAdultOnly: false };
            }
            return img;
          },
        );
      }
    },
    setCompositeTestImageBlure(
      state,
      action: PayloadAction<{ poll_id: number; img_id: string }>,
    ) {
      if (state.pollDetails?.data && 'composite' in state.pollDetails.data) {
        state.pollDetails.data.composite = state.pollDetails.data.composite.map(
          poll => {
            if (String(poll.id) === String(action.payload.poll_id)) {
              return {
                ...poll,
                images: poll.images.map(img => {
                  if (String(img.id) === String(action.payload.img_id)) {
                    return { ...img, isBlurred: false };
                  }
                  return img;
                }),
              };
            }
            return poll;
          },
        );
      }
    },
    setCompositeTestImageAdult(
      state,
      action: PayloadAction<{ poll_id: number; img_id: string }>,
    ) {
      if (state.pollDetails?.data && 'composite' in state.pollDetails.data) {
        state.pollDetails.data.composite = state.pollDetails.data.composite.map(
          poll => {
            if (String(poll.id) === String(action.payload.poll_id)) {
              return {
                ...poll,
                images: poll.images.map(img => {
                  if (String(img.id) === String(action.payload.img_id)) {
                    return { ...img, isAdultOnly: false };
                  }
                  return img;
                }),
              };
            }
            return poll;
          },
        );
      }
    },
    // Слот результата для демо-теста. Боевой fetchPollDetails этот массив и так создаёт сам.
    setDemoPollResult(
      state,
      action: PayloadAction<{ poll_id: number; data: TSinglePollDetails }>,
    ) {
      state.surveyResults.stage = 0;
      state.surveyResults.result = [
        { poll_id: action.payload.poll_id, message: '' },
      ];
      state.pollDetails.data = action.payload.data;
      state.pollDetails.loading = false;
      state.pollDetails.error = undefined;
    },
    //  Обработка результатов
    // Сохранение сообщения
    setPollMessage(
      state,
      action: PayloadAction<{ poll_id: number; message: string }>,
    ) {
      if (state.surveyResults.result) {
        state.surveyResults.result = state.surveyResults.result.map(item =>
          String(item.poll_id) === String(action.payload.poll_id)
            ? { ...item, message: action.payload.message }
            : item,
        );
      }
    },

    // Сохранение медиа-ответа
    setPollAnswerMedia(
      state,
      action: PayloadAction<{
        poll_id: number;
        answer_media: TAnswerMedia | undefined;
      }>,
    ) {
      if (state.surveyResults.result) {
        state.surveyResults.result = state.surveyResults.result.map(item =>
          item.poll_id === action.payload.poll_id
            ? { ...item, answer_media: action.payload.answer_media }
            : item,
        );
      }

      if (
        !state.pollDetails.data ||
        !('composite' in state.pollDetails.data) ||
        action.payload.answer_media?.link.startsWith('blob:')
      ) {
        return;
      }

      localStorage.setItem(
        CONFIG.POLL_PROCESS,
        JSON.stringify({
          ...state.surveyResults,
          points: state.points,
          images: state.pollDetails.data.composite.map(part => part.images),
        }),
      );
    },

    // Сохранение изображений
    setPollImages(
      state,
      action: PayloadAction<{
        poll_id: number;
        image_place: TImagePlace;
        image_id: number;
        old_image_place?: TImagePlace;
      }>,
    ) {
      if (state.surveyResults.result) {
        state.surveyResults.result = state.surveyResults.result.map(item => {
          if (item.poll_id === action.payload.poll_id) {
            if (action.payload.old_image_place) {
              return {
                ...item,
                [action.payload.old_image_place]:
                  item[action.payload.image_place],
                [action.payload.image_place]: action.payload.image_id,
              };
            }
            return {
              ...item,
              [action.payload.image_place]: action.payload.image_id,
            };
          }
          return item;
        });
      }
    },

    //Сохранение рейтинга
    setPollRate(
      state,
      action: PayloadAction<{
        poll_id: number;
        rate: number;
      }>,
    ) {
      if (state.surveyResults.result) {
        state.surveyResults.result = state.surveyResults.result.map(item =>
          item.poll_id === action.payload.poll_id
            ? { ...item, rate: action.payload.rate }
            : item,
        );
      }
    },
    // Сохранение точек
    setPollPoints(
      state,
      action: PayloadAction<{ poll_id: number; points: ClickPoint[] }>,
    ) {
      state.points[action.payload.poll_id] = action.payload.points;
    },
    clearRunData(state) {
      state.pollRun = initialState.pollRun;
    },
    clearTargetQuestionData(state) {
      state.target = initialState.target;
    },
    changeNeedAsk(state) {
      state.target.needAsk = false;
    },
    patchPollBooking(
      state,
      action: PayloadAction<{
        booked_until?: string | null;
        booking_seconds_remaining?: number | null;
      }>,
    ) {
      if (!state.pollDetails.data) {
        return;
      }

      if (action.payload.booked_until !== undefined) {
        state.pollDetails.data.booked_until = action.payload.booked_until;
      }

      if (action.payload.booking_seconds_remaining !== undefined) {
        state.pollDetails.data.booking_seconds_remaining =
          action.payload.booking_seconds_remaining;
      }
    },
  },
  extraReducers: builder => {
    // polls/fetchPollDetails
    builder
      .addCase(fetchPollDetails.pending, state => {
        state.pollDetails.loading = true;
        state.pollDetails.error = undefined;
      })
      .addCase(fetchPollDetails.fulfilled, (state, action) => {
        state.pollDetails.loading = false;
        state.pollDetails.error = undefined;
        // mappedSingleTestData
        // mappedCompositeTestData
        const details = action.payload.data.data;

        if ('composite' in details) {
          const enrichedComposite = details.composite.map(part =>
            enrichPollPart(part, state.pollsList.data),
          );
          let pollDetails = mappedCompositeTestData({
            ...details,
            composite: enrichedComposite,
          });

          const readyParts = pollDetails.composite;
          const serverResults = readyParts.map(part =>
            buildPollResultFromPart(part),
          );
          const localResults = safeJsonParse(
            localStorage.getItem(CONFIG.POLL_PROCESS),
          );
          const prevResults = localResults.success
            ? localResults.data
            : undefined;
          const draftsMatch =
            Array.isArray(prevResults?.result) &&
            prevResults.result.length === readyParts.length &&
            prevResults.result.every(item =>
              readyParts.some(part => String(part.id) === String(item.poll_id)),
            );
          const isFirstPass = readyParts.every(part => partNeedsAnswer(part));

          if (!draftsMatch) {
            if (prevResults?.result) {
              localStorage.removeItem(CONFIG.POLL_PROCESS);
            }
            state.surveyResults.result = serverResults;
          } else {
            state.surveyResults.result = serverResults.map(serverResult => {
              const part = readyParts.find(
                item => item.id === serverResult.poll_id,
              );
              if (!part || !partNeedsAnswer(part)) return serverResult;

              const draft = prevResults.result.find(
                item => String(item.poll_id) === String(serverResult.poll_id),
              );
              if (!draft) return serverResult;

              return {
                ...serverResult,
                ...draft,
                poll_id: serverResult.poll_id,
                answer_media: keepStoredAnswerMedia(
                  draft.answer_media,
                  serverResult.answer_media,
                ),
              };
            });

            if (isFirstPass && Array.isArray(prevResults.images)) {
              pollDetails = {
                ...pollDetails,
                composite: pollDetails.composite.map((item, i) => ({
                  ...item,
                  images: prevResults.images[i] || item.images,
                })),
              };
            }
          }

          readyParts.forEach(part => {
            const points = pointsFromPart(part);
            if (points.length) {
              state.points[part.id] = points;
            }
          });

          if (draftsMatch && prevResults.points) {
            readyParts.forEach(part => {
              if (!partNeedsAnswer(part)) return;
              const draftPoints = prevResults.points[part.id];
              if (draftPoints) {
                state.points[part.id] = draftPoints;
              }
            });
          }

          state.pollDetails.data = {
            ...pollDetails,
            composite: withReworkBaseline(
              pollDetails.composite,
              state.surveyResults.result || [],
            ),
          };

          state.surveyResults.stage = getAnswerStage(
            readyParts,
            action.meta.arg.partId,
          );
        } else if ('id' in details) {
          const part = enrichPollPart(details, state.pollsList.data);
          const mappedPart = mappedSingleTestData(part);
          const result = buildPollResultFromPart(part);
          state.surveyResults.stage = 0;
          state.surveyResults.result = [result];
          state.pollDetails.data = withReworkBaseline(
            [mappedPart],
            [result],
          )[0];
          const points = pointsFromPart(part);
          if (points.length) {
            state.points[part.id] = points;
          }
        }
      })
      .addCase(fetchPollDetails.rejected, (state, action) => {
        state.pollDetails.loading = false;
        const message = (action.payload as NetworkError).message;
        state.pollDetails.error = {
          ...(action.payload as NetworkError),
          message,
        };
      });

    // polls/fetchTargetQuestion
    builder
      .addCase(fetchTargetQuestion.pending, state => {
        state.target.loading = true;
        state.target.error = undefined;
      })
      .addCase(fetchTargetQuestion.fulfilled, (state, action) => {
        state.target.loading = false;
        state.target.error = undefined;
        state.target.data = action.payload.data.data;
        state.target.needAsk = false;
      })
      .addCase(fetchTargetQuestion.rejected, (state, action) => {
        state.target.loading = false;
        const message = (action.payload as NetworkError).message;
        state.target.error = {
          ...(action.payload as NetworkError),
          message,
        };
      });
    // polls/postPollResults
    builder
      .addCase(postPollResults.pending, state => {
        state.surveyResults.loading = true;
        state.surveyResults.error = undefined;
      })
      .addCase(postPollResults.fulfilled, (state, action) => {
        state.surveyResults.loading = false;
        state.surveyResults.error = undefined;
        const count = localStorage.getItem(TEST_COUNT);
        if (!count) {
          if (
            action.payload?.data?.data?.target_question &&
            action.payload?.data?.data?.target_question?.id &&
            action.payload?.data?.data?.target_question?.question &&
            action.payload?.data?.data?.target_question?.options?.length
          ) {
            state.target.needAsk = true;
            state.target.isRun = 'poll';
            state.target.data = action.payload.data.data.target_question;
          }
          localStorage.setItem(TEST_COUNT, '1');
        } else if (+count) {
          if (+count < 4) {
            localStorage.setItem(TEST_COUNT, `${+count + 1}`);
          } else {
            localStorage.removeItem(TEST_COUNT);
          }
        }
        state.surveyResults.isSended = true;
      })
      .addCase(postPollResults.rejected, (state, action) => {
        state.surveyResults.loading = false;
        const message = (action.payload as NetworkError).message;
        state.surveyResults.error = {
          ...(action.payload as NetworkError),
          message,
        };
      });

    // polls/postPollStart
    builder.addCase(postPollStart.fulfilled, (state, action) => {
      const bookedUntil = action.payload.data?.data?.booked_until;
      const bookingSecondsRemaining =
        action.payload.data?.data?.booking_seconds_remaining;

      if (state.pollDetails.data && bookedUntil !== undefined) {
        state.pollDetails.data.booked_until = bookedUntil;
      }

      if (state.pollDetails.data && bookingSecondsRemaining !== undefined) {
        state.pollDetails.data.booking_seconds_remaining =
          bookingSecondsRemaining;
      }
    });

    // polls/postPollBook
    builder
      .addCase(postPollBook.pending, state => {
        state.pollBook.loading = true;
        state.pollBook.error = undefined;
      })
      .addCase(postPollBook.fulfilled, (state, action) => {
        state.pollBook.loading = false;
        state.pollBook.error = undefined;
        state.pollBook.time = new Date().toISOString();

        const bookedUntil = action.payload.data?.data?.booked_until;
        const bookingSecondsRemaining =
          action.payload.data?.data?.booking_seconds_remaining;

        if (state.pollDetails.data && bookedUntil !== undefined) {
          state.pollDetails.data.booked_until = bookedUntil;
        }

        if (state.pollDetails.data && bookingSecondsRemaining !== undefined) {
          state.pollDetails.data.booking_seconds_remaining =
            bookingSecondsRemaining;
        }
      })
      .addCase(postPollBook.rejected, (state, action) => {
        state.pollBook.loading = false;
        const message = (action.payload as NetworkError).message;
        state.pollBook.error = {
          ...(action.payload as NetworkError),
          message,
        };
      });

    // polls/postPollRefuse
    builder
      .addCase(postPollRefuse.pending, state => {
        state.pollRefuse.loading = true;
        state.pollRefuse.error = undefined;
      })
      .addCase(postPollRefuse.fulfilled, state => {
        state.pollRefuse.loading = false;
        state.pollRefuse.error = undefined;
      })
      .addCase(postPollRefuse.rejected, (state, action) => {
        state.pollRefuse.loading = false;
        const message = (action.payload as NetworkError).message;
        state.pollRefuse.error = {
          ...(action.payload as NetworkError),
          message,
        };
      });

    // polls/fetchPollsList
    builder
      .addCase(fetchPollsList.pending, state => {
        state.pollsList.loading = true;
        state.pollsList.error = undefined;
      })
      .addCase(fetchPollsList.fulfilled, (state, action) => {
        state.pollsList.loading = false;
        state.pollsList.error = undefined;
        // Структура ответа: { data: { data: ... } }
        state.pollsList.data = action.payload.data.data;
        state.pollsList.meta = action.payload.data.meta;
        state.pollsList.init = true;
      })
      .addCase(fetchPollsList.rejected, (state, action) => {
        state.pollsList.loading = false;
        const message = (action.payload as NetworkError).message;
        state.pollsList.error = {
          ...(action.payload as NetworkError),
          message,
        };
      });

    // polls/fetchPollRun
    builder
      .addCase(fetchPollRun.pending, state => {
        state.pollRun.loading = true;
        state.pollRun.error = undefined;
      })
      .addCase(fetchPollRun.fulfilled, (state, action) => {
        state.pollRun.loading = false;
        state.pollRun.error = undefined;
        state.pollRun.data = action.payload.data.data;
        if (
          action.payload.data.data.run_poll_blocked_before &&
          action.payload?.data?.data?.target_question &&
          action.payload?.data?.data?.target_question?.id &&
          action.payload?.data?.data?.target_question?.question &&
          action.payload?.data?.data?.target_question?.options?.length
        ) {
          state.target.needAsk = true;
          state.target.data = action.payload.data.data.target_question;
          state.target.isRun = 'run';
        }
      })
      .addCase(fetchPollRun.rejected, (state, action) => {
        state.pollRun.loading = false;
        const message = (action.payload as NetworkError).message;
        state.pollRun.error = {
          ...(action.payload as NetworkError),
          message,
        };
      });

    // polls/fetchTargetAnswer
    builder
      .addCase(fetchTargetAnswer.pending, state => {
        state.target.answerLoading = true;
        state.target.answerError = undefined;
      })
      .addCase(fetchTargetAnswer.fulfilled, state => {
        state.target.answerLoading = false;
        state.target.answerError = undefined;
        state.target.answer = true;
      })
      .addCase(fetchTargetAnswer.rejected, (state, action) => {
        state.target.answerLoading = false;
        const message = (action.payload as NetworkError).message;
        state.target.answerError = {
          ...(action.payload as NetworkError),
          message,
        };
      });
  },
});
export const { actions: pollsActions } = pollsSlice;
export const { reducer: pollsReducer } = pollsSlice;
